/**
 * Nuvei Fields Style Mapper
 *
 * Converts Altruon styleCustomization object to Nuvei Fields style format.
 *
 * Nuvei Fields accepts styles in this structure:
 * {
 *   base:    { ... },   // Default state
 *   invalid: { ... },   // Validation error state
 *   valid:   { ... },   // Fully valid state
 *   empty:   { ... },   // Empty / unfilled state
 * }
 *
 * Supported CSS attributes (subset): color, fontFamily, fontSize,
 * fontSmoothing, fontStyle, fontWeight, lineHeight, letterSpacing,
 * ::placeholder, :focus, :hover, opacity, display
 *
 * @see https://docs.nuvei.com/documentation/accept-payment/web-sdk/nuvei-fields/#styling
 */

/**
 * Maps Altruon style object to Nuvei Fields style format.
 *
 * @param {Object} altruonStyles - The Altruon styleCustomization object from StyleContext
 * @returns {Object} Nuvei Fields compatible style object
 */
export const mapStylesToNuveiFields = (altruonStyles) => {
  if (!altruonStyles) return getDefaultNuveiFieldsStyles();

  const {
    components = {},
    colors = {},
    typography = {},
  } = altruonStyles;

  const input = components.input || {};

  const textColor = input.textColor || colors.textPrimary || '#111827';
  const finalTextColor =
    textColor && textColor !== 'transparent' && textColor !== 'rgba(0,0,0,0)'
      ? textColor
      : '#111827';

  return {
    base: {
      color: finalTextColor,
      fontSize: input.fontSize || typography.fontSizes?.md || '16px',
      fontFamily: typography.fontFamily || 'Arial, sans-serif',
      fontWeight: input.fontWeight || typography.fontWeights?.normal || '400',
      fontSmoothing: 'antialiased',
      lineHeight: typography.lineHeight?.normal || '1.5',
      letterSpacing: typography.letterSpacing?.normal || 'normal',
      '::placeholder': {
        color: input.placeholderColor || '#9CA3AF',
      },
    },
    invalid: {
      color: colors.error || components.errorMessage?.color || '#EF4444',
    },
    valid: {
      color: colors.success || colors.primary || '#10B981',
    },
    empty: {
      color: input.placeholderColor || '#9CA3AF',
      '::placeholder': {
        color: input.placeholderColor || '#9CA3AF',
      },
    },
  };
};

/**
 * Returns default Nuvei Fields styles (fallback when no StyleContext available).
 *
 * @returns {Object} Default Nuvei Fields style object
 */
const getDefaultNuveiFieldsStyles = () => ({
  base: {
    color: '#111827',
    fontSize: '16px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: '400',
    fontSmoothing: 'antialiased',
    lineHeight: '1.5',
    letterSpacing: 'normal',
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
  invalid: {
    color: '#EF4444',
  },
  valid: {
    color: '#10B981',
  },
  empty: {
    color: '#9CA3AF',
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
});

export default {
  mapStylesToNuveiFields,
};
